import { dsClass } from '../../../common/constants/dsClasses.js';
import { t } from '../../../common/i18n/index.js';
import { getConfiguredCommercialProducts } from '../../../common/utils/cartProductUtils.js';
import { DeviceCheckoutThankYouDeliveryDetails } from '../../DeviceCheckoutThankYou/DeviceCheckoutThankYouDeliveryDetails.js';
import { OrderSummary, OrderSummaryType } from '../../OrderSummary/OrderSummary.js';
import { PunchoutBillingInfo } from './PunchoutBillingInfo.js';
import { PunchoutButton } from './PunchoutButton.js';
import { PunchoutProducts } from './PunchoutProducts.js';
import { PunchoutUserInfo } from './PunchoutUserInfo.js';
import type { ShoppingCartItemForCheckout } from '../../../common/types/checkout.js';
import type { DeviceChangeRequest } from '../../../common/types/device.js';
import type { Address, BillingAccount } from '../../../generated/api/models.js';
import type { AuthenticatedUser } from '../../../public/site/authState';
import type { DeviceCheckoutDeliveryDetailsType } from '../../DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils.js';

import './PunchoutCheckoutConfirmation.scss';

type PunchoutCheckoutConfirmationProps = {
  cartItems: ShoppingCartItemForCheckout[];
  catalogCode: string;
  deliveryDetails: DeviceCheckoutDeliveryDetailsType;
  deviceChangeRequest?: DeviceChangeRequest;
  user: AuthenticatedUser;
  personBillingAccount?: BillingAccount;
  skipDeliveryDetails: boolean;
  shipmentTypeSelectionEnabled: boolean;
};

export const PunchoutCheckoutConfirmation = ({
  cartItems,
  catalogCode,
  deliveryDetails,
  deviceChangeRequest,
  personBillingAccount,
  user,
  skipDeliveryDetails,
  shipmentTypeSelectionEnabled,
}: PunchoutCheckoutConfirmationProps) => {
  const cartItemsCommercialProducts = getConfiguredCommercialProducts(cartItems);

  return (
    <div className="punchout-checkout-confirmation">
      <h1 className={`of-main-header ${dsClass.H2}`}>{t.IH3X('Checkout')} </h1>
      <div className={`${dsClass.TEXT_M} ${dsClass.MARGIN_BOTTOM_5}`}>
        {t.TPTZ(
          'Please check the contents of your order. After that you will be transferred back to your employer´s portal where you can finish and confirm your order.'
        )}
      </div>
      <PunchoutUserInfo user={user} className={`${dsClass.MARGIN_TOP_7} ${dsClass.MARGIN_BOTTOM_7}`} />
      {personBillingAccount && (
        <PunchoutBillingInfo personBillingAccount={personBillingAccount} className={dsClass.MARGIN_BOTTOM_8} />
      )}
      <PunchoutProducts cartItems={cartItems} />
      {deliveryDetails && (
        <DeviceCheckoutThankYouDeliveryDetails
          deliveryDetails={deliveryDetails}
          skipDelivery={skipDeliveryDetails && !shipmentTypeSelectionEnabled}
          companyName={user.companyName}
          shipmentType={deliveryDetails.shipmentType}
          shipmentTypeEnabled={shipmentTypeSelectionEnabled}
        />
      )}
      <OrderSummary
        summaryType={OrderSummaryType.DETAILS_CLOSED}
        commercialProducts={cartItemsCommercialProducts}
        classes={[`${dsClass.MARGIN_TOP_7} ${dsClass.MARGIN_BOTTOM_7}`]}
        priceIncludesVAT
      />
      <PunchoutButton
        cartItems={cartItems}
        catalogCode={catalogCode}
        personBillingAccountId={personBillingAccount?.billingAccountId}
        deviceChangeRequest={deviceChangeRequest}
        className={dsClass.PADDING_TOP_4}
        shipmentType={shipmentTypeSelectionEnabled ? deliveryDetails.shipmentType : undefined}
        deliveryAddress={
          !skipDeliveryDetails
            ? {
                companyName: deliveryDetails.companyName || user.companyName,
                recipient: deliveryDetails.recipientName || '',
                address: deliveryDetails.address ?? ({} as Address),
                phoneNumber: deliveryDetails.recipientPhoneNumber || '',
              }
            : undefined
        }
      />
    </div>
  );
};
